// PhoneControl.tsx
import React, { useEffect, useState } from "react";
import { Controller, Control, FieldValues, Path, useWatch, useFormContext } from "react-hook-form";
import { Dropdown } from "primereact/dropdown";
import { InputMask } from "primereact/inputmask";
import { isValidPhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";
import { FloatLabel } from "primereact/floatlabel";
import { cn } from "@/components/ui/cn";

type CountryConfig = {
	code: CountryCode;
	label: string;
	mask: string;
	hint: string;
};

// const COUNTRIES: CountryConfig[] = [
// 	{ code: "BR", label: "🇧🇷 Brazil", mask: "(99) 99999-9999", hint: "(11) 91234-5678" },
// 	{ code: "US", label: "🇺🇸 USA", mask: "(999) 999-9999", hint: "(415) 555-2671" },
// 	{ code: "CA", label: "🇨🇦 Canada", mask: "(999) 999-9999", hint: "(416) 555-2671" },
// 	{ code: "GB", label: "🇬🇧 UK", mask: "9999 999 9999", hint: "0207 123 4567" },
// 	{ code: "RU", label: "🇷🇺 Russia", mask: "9 (999) 999-99-99", hint: "9 (912) 345-67-89" }
// ];

interface PhoneControlProps<T extends FieldValues> {
	control: Control<T>;
	countryCode: string;
	id: Path<T>;
	defaultCountry?: CountryCode;
}

export function PhoneControl<T extends FieldValues>({
	control,
	countryCode,
	id,
	defaultCountry = "BR"
}: PhoneControlProps<T>) {
	const [value, setValue] = useState("");
	const [focused, setFocused] = useState(false);

	/* 1. We use Phillippine template for all countries - 10 digits. Because implementing it perfectly is nearly impossible.
	2. Do not delete spaces at the end. It is fallback to allow more digits in the number. GPT tells that 15 is maximum existing on the Earth. So, we have 10 zeroes to satisfy design in figma but at the same time are flexible in possible numbers. Bear in mind, that some contries may have different number length depending on province. E.g. Germany and Italy.*/
	const template = "000 000 0000      ";
	const mask = '999 999 9999999999';

	// build ghost: typed digits stay hidden, unused slots show grey zeros
	const ghost = template.split("").map((ch, i) => {
		if (/\d/.test(ch)) {
			const typed = value.replace(/\D/g, ""); // only digits
			const digitIndex = template.substring(0, i).replace(/\D/g, "").length;
			return typed[digitIndex] ? " " : "0"; // show space if digit exist, 0 if empty
		}

		return ch; // keep spaces/dashes
	}).join("");

	//console.log(`value: '${ghost}'`)
	return (
		<>
			{/* Phone input */}
			<Controller
				name={id}
				control={control}
				rules={{
					required: "Phone is required",
					validate: (v, formValues) =>
						isValidPhoneNumber(v, formValues[countryCode]) || "Invalid phone"
				}}
				render={({ field, fieldState, formState }) => {
					const country = formState.defaultValues?.[countryCode] || defaultCountry;
					//const cfg = COUNTRIES.find(c => c.code === (countryCode ?? country)) ?? COUNTRIES[0];
					return (
						<div className="flex w-full flex-col  ">
							<FloatLabel unstyled className='flex w-full relative h-[56px]'>

								{/* Ghost layer to have zeroes mask. Such solution is generally fine with two problems for now:
							1. The font should be monospace. Otherwise we have problems because of diffent width of different widgits. E.g. 1 an 8.
							2. We rely on the fact that incomplete value gets wiped on blur. Otherwise it would be more difficult.
							3. Mask zeroes need to be gray to distinguish from actual digits. But this is actually fixeds design error. In glutter app we have the same.
							4. When the number is invalid and icomplete we have crazy 1 pixel vertical shift of number from mask. The problem is that it is affected by errors in other controls. E.g. when there is an error in first name we have the shift changing in the phone number. So, currently I see no oppotunity to fix it even with some hack. If I set font to poppings the shift disappers but see point 1. Another option is to use the ghost layer just as placeholder - hide it when typing starts. Why not placeholer property? Because it blinks and glitches...
							*/}
								<div
									aria-hidden
									className={cn(
										"absolute w-full rounded-t-[4px] h-[56px] pl-[16px] pt-[23px] pb-[8px]",
										"text-lg  font-mono text-onSurface bg-surfaceContainerHighest",
										"border-b border-b-onSurfaceVariant",
										"pointer-events-none select-none"
									)}
									style={{
										whiteSpace: "pre", color: "#9ca3af",
										// not suppoerted by the font: fontVariantNumeric: "tabular-nums"
									}}
								>
									{ghost}
								</div>
								<InputMask
									{...field}
									unstyled
									className={cn("peer w-full text-lg absolute  pt-[26px] h-[56px] border-b-onSurfaceVariant pl-[16px] pb-[8px] outline-none border-b-[1px] bg-transparent font-mono",
										//"focus:border-b-[3px] focus: pb-[4px] border-transparent focus:border-b-primary",
										"focus:border-transparent focus:border-b-primary",
										// Using border on focus makes the text jump. So, only shadow.
										"focus:[box-shadow:inset_0_-2px_0_0_theme(colors.primary)]",
										fieldState.error && "focus:border-b-error focus:[box-shadow:inset_0_-2px_0_0_theme(colors.error)]",
										!field.value && "bg-surfaceContainerHighest focus:bg-transparent"
									)}
									mask={mask}
									id='phone_input'
									name='phone_input'
									/*Slot chars and real digits are mixed into the value. So, we can't style them differently.	Need ghost layer.*/
									slotChar=" "
									autoClear={false}
									placeholder={''}
									onChange={(e) => {

										field.onChange(e.value);
										setValue(e.value || "");
									}}
									onFocus={() => setFocused(true)}
									onBlur={() => setFocused(false)}
									style={{
										//fontVariantNumeric: "tabular-nums"
									}}
								/>

								<label htmlFor="phone_input"
									className={cn(
										// Base label styles from .p-float-label > label
										"absolute left-4 transition-all duration-200 pointer-events-none text-onSurfaceVariant text-lg font-poppins",
										"mt-[16px]",
										// Filled state (when input has value) - from .default-input.p-filled+label
										field.value && "text-sm text-primary mt-1",

										// Focus state - from .default-input .p-inputtext:focus+label
										"peer-focus:text-sm peer-focus:text-primary peer-focus:mt-1",
										// Error state - from .p-float-label > label.error-label
										fieldState.error && "text-error text-lg",
										field.value && "text-sm",
										fieldState.error && "peer-focus:text-error peer-focus:text-sm",

										fieldState.error && "peer-focus:mt-1",

										// error && "peer-focus:mt-1",
										// // Custom label styles
										// typeof labelClassName === 'function'
										// 	? labelClassName(field)
										// 	: labelClassName
									)}
								>Mobile No.</label>

							</FloatLabel>
							{/* Error message */}
							{fieldState.error && (
								<p className="text-[10px] text-error mt-1 ml-4 font-poppins">
									{fieldState.error.message}
								</p>
							)}
						</div>
					);
				}}
			/>
		</>
	);
}
